import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { DocumentUploadDialog } from "@/components/document-upload-dialog";
import { Paperclip, FileText, Trash2, ExternalLink, Plus, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import type { Expense } from "@shared/schema";

interface AttachmentItem {
  id: number;
  documentId: number;
  expenseId: number;
  fileName: string;
  mimeType: string;
  uploadedAt: string;
  attachedAt: string;
  fileUrl: string;
}

interface UnlinkedDoc {
  id: number;
  fileName: string;
  mimeType: string;
  uploadedAt: string;
  category: string;
}

interface MergeCandidate {
  id: number;
  manualExpenseId: number;
  matchScore: number;
  manualExpense: Expense;
}

interface Props {
  open: boolean;
  onClose: () => void;
  expense: Expense & { documentFileName?: string | null };
  propertyId: number | null;
  readOnly?: boolean;
}

function fmtDate(d: string | Date) {
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}

export function ExpenseAttachmentDrawer({ open, onClose, expense, propertyId, readOnly }: Props) {
  const { toast } = useToast();
  const [showPickExisting, setShowPickExisting] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  const { data: attachments = [], isLoading: loadingAttachments } = useQuery<AttachmentItem[]>({
    queryKey: ["/api/app/expenses", expense.id, "attachments"],
    queryFn: async () => {
      const res = await fetch(`/api/app/expenses/${expense.id}/attachments`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load attachments");
      return res.json();
    },
    enabled: open,
  });

  const { data: unlinkedDocs = [], isLoading: loadingUnlinked } = useQuery<UnlinkedDoc[]>({
    queryKey: ["/api/app/expenses", expense.id, "unlinked-documents"],
    queryFn: async () => {
      const res = await fetch(`/api/app/expenses/${expense.id}/unlinked-documents`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load documents");
      return res.json();
    },
    enabled: open && showPickExisting,
  });

  const { data: mergeCandidates = [], isLoading: loadingCandidates } = useQuery<MergeCandidate[]>({
    queryKey: ["/api/app/expenses/merge-candidates-for", expense.id],
    queryFn: async () => {
      const res = await fetch(`/api/app/expenses/merge-candidates`, { credentials: "include" });
      if (!res.ok) return [];
      const all = await res.json() as { id: number; mercuryExpenseId: number; manualExpenseId: number; matchScore: number; manualExpense: Expense; status: string }[];
      return all
        .filter(c => c.mercuryExpenseId === expense.id && (c.status === "open" || c.status === "snoozed"))
        .map(c => ({ id: c.id, manualExpenseId: c.manualExpenseId, matchScore: c.matchScore, manualExpense: c.manualExpense }));
    },
    enabled: open && showMerge && expense.externalSource === "mercury",
  });

  const removeMutation = useMutation({
    mutationFn: async (docId: number) => {
      await apiRequest("DELETE", `/api/app/expenses/${expense.id}/attachments/${docId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", expense.id, "attachments"] });
      toast({ title: "Attachment removed" });
    },
    onError: (e: any) => toast({ title: "Remove failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const attachMutation = useMutation({
    mutationFn: async (documentId: number) => {
      const res = await apiRequest("POST", `/api/app/expenses/${expense.id}/attachments`, { documentId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", expense.id, "attachments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", expense.id, "unlinked-documents"] });
      setShowPickExisting(false);
      toast({ title: "Document attached" });
    },
    onError: (e: any) => toast({ title: "Attach failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const mergeMutation = useMutation({
    mutationFn: async (manualId: number) => {
      const res = await apiRequest("POST", `/api/app/expenses/${expense.id}/merge-with/${manualId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", expense.id, "attachments"] });
      setShowMerge(false);
      toast({ title: "Expenses matched", description: "The manual expense has been merged into this Mercury transaction." });
      onClose();
    },
    onError: (e: any) => toast({ title: "Match failed", description: parseErrorMessage(e, "Please try again."), variant: "destructive" }),
  });

  const isMercury = expense.externalSource === "mercury";

  // In-flight dismissal gating (docs/dialog-dismissal-convention.md). Attach /
  // merge / remove are real writes (merge even closes the drawer on success);
  // a stray X-button / Escape / backdrop click mid-request would drop the
  // action, so block every dismissal path while one is in flight.
  const isInFlight =
    attachMutation.isPending || mergeMutation.isPending || removeMutation.isPending;

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => { if (!v && isInFlight) return; if (!v) onClose(); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto"
          onEscapeKeyDown={(e) => { if (isInFlight) e.preventDefault(); }}
          onInteractOutside={(e) => { if (isInFlight) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (isInFlight) e.preventDefault(); }}
        >
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </SheetTitle>
            <p className="text-sm text-gray-500 mt-1">
              {expense.description || expense.vendor || "Expense"} — {expense.date}
            </p>
          </SheetHeader>

          <div className="space-y-4">
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Attached Documents
                {attachments.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-medium px-1.5 min-w-[1.25rem] h-5">
                    {attachments.length}
                  </span>
                )}
              </h3>

              {loadingAttachments ? (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : attachments.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">No documents attached yet.</p>
              ) : (
                <ul className="space-y-2">
                  {attachments.map((att) => (
                    <li
                      key={att.id}
                      className="flex items-center justify-between gap-2 p-2 rounded-lg border border-gray-100 bg-gray-50"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate" title={att.fileName}>{att.fileName}</p>
                          <p className="text-xs text-gray-400">{fmtDate(att.uploadedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <a
                          href={att.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600 p-1"
                          title="Open document"
                          data-testid={`link-open-attachment-${att.documentId}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        {!readOnly && (
                          <button
                            onClick={() => removeMutation.mutate(att.documentId)}
                            disabled={removeMutation.isPending}
                            className="text-gray-400 hover:text-red-600 p-1 disabled:opacity-50"
                            title="Remove attachment"
                            data-testid={`button-remove-attachment-${att.documentId}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {!readOnly && propertyId && (
              <section className="space-y-2 pt-2 border-t border-gray-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() => setShowUpload(true)}
                  data-testid="button-upload-new-document"
                >
                  <Plus className="h-4 w-4" />
                  Upload new document
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() => setShowPickExisting((v) => !v)}
                  data-testid="button-attach-existing-document"
                >
                  {showPickExisting ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Attach existing document
                </Button>

                {showPickExisting && (
                  <div className="pl-2">
                    {loadingUnlinked ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading documents…
                      </div>
                    ) : unlinkedDocs.length === 0 ? (
                      <p className="text-sm text-gray-400 py-2">No other documents available for this property.</p>
                    ) : (
                      <ul className="space-y-1 max-h-52 overflow-y-auto">
                        {unlinkedDocs.map((doc) => (
                          <li key={doc.id}>
                            <button
                              onClick={() => attachMutation.mutate(doc.id)}
                              disabled={attachMutation.isPending}
                              className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50 text-sm"
                              data-testid={`button-pick-document-${doc.id}`}
                            >
                              <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate font-medium text-gray-700">{doc.fileName}</span>
                              <span className="text-xs text-gray-400 flex-shrink-0">{fmtDate(doc.uploadedAt)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>
            )}

            {!readOnly && isMercury && (
              <section className="pt-2 border-t border-gray-100">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  onClick={() => setShowMerge((v) => !v)}
                  data-testid="button-match-with-expense"
                >
                  {showMerge ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Match with uploaded expense
                </Button>

                {showMerge && (
                  <div className="pl-2 mt-2">
                    <p className="text-xs text-gray-500 mb-2">
                      Candidates are ranked by amount + date match. Selecting one merges the manual row into this Mercury transaction and transfers its document.
                    </p>
                    {loadingCandidates ? (
                      <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Finding candidates…
                      </div>
                    ) : mergeCandidates.length === 0 ? (
                      <p className="text-sm text-gray-400 py-2">No reconciliation candidates found. Use the Reconcile panel to score candidates first.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {mergeCandidates.map((c) => (
                          <li key={c.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-amber-100 bg-amber-50">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {c.manualExpense.description || c.manualExpense.vendor || "Manual expense"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {c.manualExpense.date} · ${parseFloat(c.manualExpense.amount || "0").toFixed(2)} · Score: {Math.round(c.matchScore)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => mergeMutation.mutate(c.manualExpenseId)}
                              disabled={mergeMutation.isPending}
                              data-testid={`button-confirm-merge-${c.manualExpenseId}`}
                              className="flex-shrink-0"
                            >
                              {mergeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm Match"}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {showUpload && propertyId && (
        <DocumentUploadDialog
          open={showUpload}
          onClose={() => setShowUpload(false)}
          propertyId={propertyId}
          mode="expense"
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
            queryClient.invalidateQueries({ queryKey: ["/api/app/expenses", expense.id, "unlinked-documents"] });
            setShowPickExisting(true);
            setShowUpload(false);
          }}
        />
      )}
    </>
  );
}
